/**
 * Pure audio processing pipeline.
 *
 * Converts an `AudioData` frame into a single mono PCM16 chunk plus a
 * bundle of per-frame statistics for diagnostics. System-loopback
 * frames are normally already mono at TARGET_SAMPLE_RATE because the
 * capture source wraps them in a 16 kHz mono Web Audio graph (see
 * `AudioCaptureSource`). Mic frames intentionally bypass that wrap to
 * preserve Chromium's native echo-cancellation path, so this module
 * still normalises any raw rate/channel shape before bytes leave the
 * renderer.
 *
 *  - Downmix the AudioData frame to mono (equal-weight channel average,
 *    format-converted to f32 by Chromium regardless of source format).
 *  - Resample to TARGET_SAMPLE_RATE when needed (StreamingResampler:
 *    band-limited, stateful across frames — see streaming-resampler.ts
 *    for why linear interpolation was not enough).
 *  - Walk it once for RMS / peak / NaN / clip stats.
 *  - PCM16 conversion.
 *  - Track inter-frame timestamp / wall-clock deltas for diagnostics.
 *
 * State: two cached timestamps for inter-frame deltas, plus the
 * resampler's carried filter history. Both are dropped on reset().
 *
 * NOT in scope: WebSocket transport, buffering, channel lifecycle,
 * channel mixing, diagnostics rendering. Those live elsewhere so this
 * file stays unit-testable in isolation.
 */
import { TARGET_SAMPLE_RATE, type ChannelLabel } from './types'
import { StreamingResampler } from './streaming-resampler'

export function floatToPcm16(f32: Float32Array): ArrayBuffer {
  const pcm = new Int16Array(f32.length)
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]))
    // Round (don't truncate) to halve the quantization error. NaN input
    // stays NaN through Math.round and coerces to 0 on Int16Array
    // assignment — the sanitization audio-channel relies on.
    pcm[i] = Math.round(s < 0 ? s * 0x8000 : s * 0x7fff)
  }
  return pcm.buffer
}

/** Downmix one AudioData frame to mono f32. Uses copyTo's format
 *  conversion (Chromium ≥ 106; we ship 150) so any source format —
 *  planar or interleaved, int or float — lands as f32 planes without
 *  format-specific branches. Interleaved sources previously made the
 *  raw plane-0 copy throw and killed the capture read loop.
 *
 *  Channels are averaged with equal weight, matching the Web Audio
 *  wrap's 'speakers' stereo downmix (0.5·(L+R)) on the system channel;
 *  plane-0-only picked the wrong signal on interfaces that put the mic
 *  on another channel. Exported for tests. */
export function copyToMonoFloat32(audioData: AudioData): Float32Array {
  const numFrames = audioData.numberOfFrames
  const numChannels = audioData.numberOfChannels
  const mono = new Float32Array(numFrames)
  if (numChannels <= 0) {
    return mono
  }
  if (numChannels === 1) {
    audioData.copyTo(mono, { planeIndex: 0, format: 'f32-planar' })
    return mono
  }
  const plane = new Float32Array(numFrames)
  for (let c = 0; c < numChannels; c++) {
    audioData.copyTo(plane, { planeIndex: c, format: 'f32-planar' })
    for (let i = 0; i < numFrames; i++) {
      mono[i] += plane[i]
    }
  }
  const gain = 1 / numChannels
  for (let i = 0; i < numFrames; i++) {
    mono[i] *= gain
  }
  return mono
}

export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i]
  return Math.sqrt(sum / samples.length)
}

export interface AudioPcmFrame {
  /** PCM16 mono bytes, ready for the WS. */
  pcm: ArrayBuffer
  /** Mono downmix at the source rate, BEFORE any resample. Freshly
   *  allocated per frame and not retained by the pipeline — the
   *  channel's debug tap records it (audio-debug mode only) so the
   *  resampler's input and output can be A/B'd by ear. Zeroed by the
   *  channel alongside `pcm` when the mic gate mutes the frame. */
  rawMono: Float32Array
  /** Number of input frames pulled from AudioData before any resample. */
  numFrames: number
  /** Number of input planes (channels) in the source frame. Usually 1
   *  for Web-Audio-wrapped system loopback; raw mic frames may expose
   *  the device's native channel count. */
  numChannels: number
  /** Rate the source claimed (`audioData.sampleRate`) before any resample. */
  inputRate: number
  /** RMS of the signal sent downstream. */
  energy: number
  /** Max absolute sample value seen in the signal sent downstream. */
  peakAbs: number
  /** Count of NaN / non-finite samples observed. */
  nanCount: number
  /** Count of samples whose absolute value met or exceeded the
   *  clipping threshold (0.999). */
  clipCount: number
  /** AudioData.timestamp at which this frame was produced (microseconds). */
  timestampUs: number
  /** performance.now() at the moment the frame was processed (milliseconds). */
  wallNowMs: number
  /** Audio-time delta to the previous frame from the same pipeline (microseconds);
   *  null on the first frame after construction or reset(). */
  tsDeltaUs: number | null
  /** Wall-clock delta to the previous frame from the same pipeline (milliseconds);
   *  null on the first frame after construction or reset(). */
  wallDeltaMs: number | null
}

/**
 * Per-channel PCM processing pipeline. Owns just the inter-frame
 * timestamp cache used by diagnostics; everything else is derived
 * from the inbound frame.
 *
 * Lifetime: one instance per AudioChannel. Survives the channel's WS
 * reconnects but should be reset() on capture restart so the
 * post-restart first frame is treated as a delta-less "first frame"
 * in the diag stream.
 */
export class AudioPcmPipeline {
  private lastFrameTimestampUs: number | null = null
  private lastFrameWallMs: number | null = null
  /** Lazily created on the first frame that needs a rate conversion;
   *  rebuilt if the input rate changes mid-stream (Bluetooth devices
   *  renegotiate between HFP 8/16/24 kHz and A2DP 44.1/48 kHz without
   *  a track restart). Frames already at TARGET_SAMPLE_RATE bypass it
   *  entirely, so the wrapped system channel pays nothing. */
  private resampler: StreamingResampler | null = null

  constructor(private readonly label: ChannelLabel) {}

  /** Drop the inter-frame timing cache and the resampler's carried
   *  stream state. Call this whenever the underlying capture source
   *  changes (capture restart, mic device switch, loopback re-acquire)
   *  or on pause/resume so the post-resume first frame is treated as a
   *  delta-less "first frame" and stale filter history isn't spliced
   *  across the gap. */
  reset(): void {
    this.lastFrameTimestampUs = null
    this.lastFrameWallMs = null
    this.resampler?.resetState()
  }

  /** Alias for `reset()` retained for callers that want the more
   *  specific intent at the call site (pause/resume vs full restart). */
  clearFrameTimings(): void {
    this.reset()
  }

  /** Route a frame's samples through the streaming resampler, creating
   *  or rebuilding it when the input rate changes. Frames already at
   *  the target rate pass through untouched. */
  private resample(samples: Float32Array, inputRate: number): Float32Array {
    if (inputRate === TARGET_SAMPLE_RATE) {
      // A device that just renegotiated down to 16 kHz leaves a stale
      // resampler behind; drop it so a later rate change starts clean.
      this.resampler = null
      return samples
    }
    if (inputRate <= 0 || samples.length === 0) {
      return new Float32Array()
    }
    if (!this.resampler || this.resampler.inputRate !== inputRate) {
      if (this.resampler) {
        console.warn(
          `[AudioTranscriptionService:${this.label}] input rate changed ${this.resampler.inputRate}Hz → ${inputRate}Hz mid-stream (device route change?) — rebuilding resampler`,
        )
      }
      this.resampler = new StreamingResampler(inputRate, TARGET_SAMPLE_RATE)
    }
    return this.resampler.process(samples)
  }

  /** Process a single AudioData frame end-to-end. Caller is responsible
   *  for closing `audioData` after this returns. Pure aside from updating
   *  the inter-frame timing state on `this`. */
  process(audioData: AudioData): AudioPcmFrame {
    const inputRate = audioData.sampleRate
    const numFrames = audioData.numberOfFrames
    const numChannels = audioData.numberOfChannels
    const timestampUs = audioData.timestamp
    const wallNowMs = performance.now()

    let tsDeltaUs: number | null = null
    let wallDeltaMs: number | null = null
    if (this.lastFrameTimestampUs !== null && this.lastFrameWallMs !== null) {
      tsDeltaUs = timestampUs - this.lastFrameTimestampUs
      wallDeltaMs = wallNowMs - this.lastFrameWallMs
    }
    this.lastFrameTimestampUs = timestampUs
    this.lastFrameWallMs = wallNowMs

    const mono = copyToMonoFloat32(audioData)
    const samples = this.resample(mono, inputRate)

    let peakAbs = 0
    let nanCount = 0
    let clipCount = 0
    for (let i = 0; i < samples.length; i++) {
      const v = samples[i]
      if (Number.isNaN(v) || !Number.isFinite(v)) {
        nanCount += 1
        continue
      }
      const abs = Math.abs(v)
      if (abs > peakAbs) peakAbs = abs
      if (abs >= 0.999) clipCount += 1
    }
    const energy = rms(samples)

    const pcm = floatToPcm16(samples)

    return {
      pcm,
      rawMono: mono,
      numFrames,
      numChannels,
      inputRate,
      energy,
      peakAbs,
      nanCount,
      clipCount,
      timestampUs,
      wallNowMs,
      tsDeltaUs,
      wallDeltaMs,
    }
  }

  /** Channel label used by callers that want to format consistent log
   *  prefixes. Exposed so the orchestrator doesn't have to track it
   *  separately. */
  get channelLabel(): ChannelLabel {
    return this.label
  }
}
